import { useState } from 'react';
import { toast } from 'sonner';
import type { CashRegisterDetailDto } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCloseRegister } from '../api';
import { MoneyInput } from '../money-input';
import { signed } from '../labels';

export function diffClass(diff: number | null | undefined) {
  if (!diff) return 'text-foreground';
  return diff < 0 ? 'text-oxblood-ink' : 'text-amber-ink';
}

export function CloseRegisterModal({ register, open, onOpenChange }: { register: CashRegisterDetailDto; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [counted, setCounted] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const mut = useCloseRegister(register.id);
  const expected = register.expectedCashNowCents;
  const diff = counted === null ? null : counted - expected;

  async function close() {
    if (counted === null) return;
    try {
      await mut.mutateAsync({ countedAmountCents: counted, notes: notes.trim() || undefined });
      toast.success('Caixa fechado.');
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Fechar caixa"
      description="Conte o dinheiro da gaveta e confira a diferença antes de confirmar."
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <ConfirmDialog
            trigger={<Button type="button" disabled={counted === null || mut.isPending}>Revisar fechamento</Button>}
            title="Confirmar fechamento do caixa?"
            description={`Esperado: ${money(expected)}. Contado: ${money(counted ?? 0)}. Diferença: ${signed(diff ?? 0, money)}. Depois de fechado, o caixa não pode ser reaberto.`}
            confirmLabel="Fechar caixa"
            onConfirm={close}
          />
        </>
      }
    >
      <div className="grid gap-4">
        <Field id="close-counted" label="Dinheiro contado na gaveta">
          <MoneyInput id="close-counted" value={counted ?? 0} onChange={setCounted} />
        </Field>
        <dl className="grid gap-2 rounded-md border bg-muted/40 p-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Dinheiro esperado</dt>
            <dd className="tabular font-medium">{money(expected)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Diferença (contado menos esperado)</dt>
            <dd className={cn('tabular font-medium', diffClass(diff))}>{diff === null ? 'Informe a contagem' : signed(diff, money)}</dd>
          </div>
        </dl>
        <Field id="close-notes" label="Observações (opcional)">
          <Textarea id="close-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2} />
        </Field>
      </div>
    </FormModal>
  );
}
